import * as Sentry from '@sentry/browser';
import get from 'lodash/get';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { font } from 'src/shared/styles/variables';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { toastNotificationsService } from 'src/config/rootService';
import { CareOption } from 'src/generated/webapp_gql';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { getSessionUtilizationInfo } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import running from 'src/shared/svg/running.svg';
import sessionTopics from 'src/shared/svg/session-topics.svg';
import soulAndMindful from 'src/shared/svg/soul-and-mindful.svg';
import trophy from 'src/shared/svg/trophy.svg';
import { useAcceptCareRecommendationMutation } from '../documents/acceptCareRecommendationMutation.generated';
import { CarePreferenceInfo } from 'src/modules/onboarding/components/CarePreferenceInfo';

export const OnMyOwnPage: React.FC<RouteComponentProps> = ({ history }) => {
    const { resetDate } = useSelector(getSessionUtilizationInfo);
    const [acceptCareRecommendation] = useAcceptCareRecommendationMutation({
        client: appClient,
    });
    const { careRecommendations } = useSelector(getUserInformation);

    const { t } = useTranslation();

    const data = [
        {
            title: t('onboarding:on_my_own_page.tiles.tile_1.title'),
            description: t('onboarding:on_my_own_page.tiles.tile_1.description'),
            img: trophy,
        },
        {
            title: t('onboarding:on_my_own_page.tiles.tile_2.title'),
            description: t('onboarding:on_my_own_page.tiles.tile_2.description'),
            img: running,
        },
        {
            title: t('onboarding:on_my_own_page.tiles.tile_3.title'),
            description: t('onboarding:on_my_own_page.tiles.tile_3.description'),
            img: sessionTopics,
        },
    ];

    const handleContinue = async () => {
        try {
            const res = await acceptCareRecommendation({
                variables: {
                    uuid: careRecommendations[0].uuid,
                    outcome: CareOption.DigitalPrograms,
                },
            });
            const careUuid = get(res, 'data.acceptCareRecommendation.careRecommendation.uuid');
            if (!careUuid) {
                throw new Error('Failed to accept care recommendation');
            }

            history.push('/get-program-recommendations');
        } catch (error) {
            history.push('/get-program-recommendations');

            toastNotificationsService.renderErrorToast(config.messages.genericError);

            Sentry.withScope(scope => {
                scope.setExtra('error_info', 'Error: failed to accept a care recommendation');

                Sentry.captureException(error);
            });
        }
    };

    return (
        <CarePreferenceInfo
            mainImage={soulAndMindful}
            tiles={data}
            title={t('onboarding:on_my_own_page.title')}
            description={
                <>
                    {t('onboarding:on_my_own_page.description')}{' '}
                    <span style={font.heavy(font.size.mediumExtended)}>
                        {t('onboarding:on_my_own_page.unlimited_digital_programs')}
                    </span>
                    {t('onboarding:on_my_own_page.till_date', {
                        date: resetDate.format('MMM Do, YYYY'),
                    })}
                </>
            }
            subtitle={t('onboarding:on_my_own_page.subtitle')}
            onContinue={handleContinue}
            onCancel={() => {
                history.push('/');
            }}
            continueLabel={t('onboarding:on_my_own_page.get_program_recommendations')}
            cancelLabel={t('onboarding:on_my_own_page.not_now')}
        />
    );
};
